const DB_NAME = "WebGameHub";
const DB_VERSION = 1;
const STORE_SAVES = "saves";
const STORE_SETTINGS = "settings";
const STORE_ROOMS = "local_rooms";
export function openDatabase(name = DB_NAME, version = DB_VERSION) {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB is not available in this environment"));
            return;
        }
        const req = indexedDB.open(name, version);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_SAVES)) {
                const store = db.createObjectStore(STORE_SAVES, { keyPath: ["gameId", "slot"] });
                store.createIndex("byGame", "gameId", { unique: false });
            }
            if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
                db.createObjectStore(STORE_SETTINGS, { keyPath: "key" });
            }
            if (!db.objectStoreNames.contains(STORE_ROOMS)) {
                db.createObjectStore(STORE_ROOMS, { keyPath: "roomId" });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error("Failed to open IndexedDB"));
    });
}
/** IndexedDB 实现（设计文档 §23）。 */
export class IndexedGameStorage {
    #db;
    #gameId;
    constructor(gameId, dbName = DB_NAME) {
        this.#gameId = gameId;
        this.#db = openDatabase(dbName);
    }
    async #tx(store, mode, fn) {
        const db = await this.#db;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(store, mode);
            const req = fn(tx.objectStore(store));
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error ?? new Error("IndexedDB transaction failed"));
            tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
        });
    }
    async save(slot, data) {
        const record = {
            gameId: this.#gameId,
            slot,
            data,
            updatedAt: Date.now(),
        };
        await this.#tx(STORE_SAVES, "readwrite", (s) => s.put(record));
    }
    async load(slot) {
        const rec = await this.#tx(STORE_SAVES, "readonly", (s) => s.get([this.#gameId, slot]));
        return rec ? rec.data : null;
    }
    async delete(slot) {
        await this.#tx(STORE_SAVES, "readwrite", (s) => s.delete([this.#gameId, slot]));
    }
    async list() {
        const db = await this.#db;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_SAVES, "readonly");
            const index = tx.objectStore(STORE_SAVES).index("byGame");
            const req = index.getAll(IDBKeyRange.only(this.#gameId));
            const out = [];
            req.onsuccess = () => {
                for (const rec of req.result) {
                    out.push({ slot: rec.slot, updatedAt: rec.updatedAt });
                }
                out.sort((a, b) => b.updatedAt - a.updatedAt);
                resolve(out);
            };
            req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
        });
    }
}
/** 内存实现：SSR / 测试 / IndexedDB 不可用时的回退。 */
export class MemoryGameStorage {
    #gameId;
    #map = new Map();
    constructor(gameId = "memory") {
        this.#gameId = gameId;
    }
    get gameId() {
        return this.#gameId;
    }
    async save(slot, data) {
        this.#map.set(slot, { data, updatedAt: Date.now() });
    }
    async load(slot) {
        return this.#map.get(slot)?.data ?? null;
    }
    async delete(slot) {
        this.#map.delete(slot);
    }
    async list() {
        return [...this.#map.entries()]
            .map(([slot, v]) => ({ slot, updatedAt: v.updatedAt }))
            .sort((a, b) => b.updatedAt - a.updatedAt);
    }
}
export function createGameStorage(gameId) {
    try {
        if (typeof indexedDB !== "undefined")
            return new IndexedGameStorage(gameId);
    }
    catch {
        // fall through to memory
    }
    return new MemoryGameStorage(gameId);
}
//# sourceMappingURL=indexeddb.js.map